import { Tooltip } from 'antd';
import React, { FC } from 'react';

import { Approved } from '@/components/modules/orders-page/orders/ui/body/approved';
import { EOrderStatus, OrderDateRentModel, TOrder } from '@/types/orders';

import { usePrepareOrderCard } from '@/entities/requisites';

import { useTranslate } from '@/shared/hooks/use-translate';
import 'dayjs/locale/ru';
import { ExclamationIcon } from '@/shared/ui/icons';
import { numbers } from '@/shared/utils/numbers';

import { WrapperDetailInfo } from '../body/wrapper-detail-info';
import { TProductItemConfig } from '../product-item';

import * as Styled from './styled';

type TProps = {
    config: TProductItemConfig;
    dateRent?: OrderDateRentModel;
    order: TOrder;
    onModalDetailsPayment: () => void;
    onModalCalendarPayment: () => void;
};

export const RentStatus: FC<TProps> = ({
    config,
    dateRent,
    onModalDetailsPayment,
    onModalCalendarPayment,
    order,
}): JSX.Element => {
    const { t } = useTranslate();
    const price = order.amounts?.return?.interest;
    const priceReturned = order.rentProduct?.rent_pledge?.apr_amount;
    const { status, termUnit } = config;
    const { onOrderValidatePay, isLoading } = usePrepareOrderCard(order);

    const tooltipText = `Задолженность включает в себя арендную плату, неустойку за нарушение условий договора,
                        стоимость товара и юридические расходы по взысканию задолженности`;

    switch (status) {
        case EOrderStatus.Approved:
            return <Approved config={config} priceReturned={priceReturned} price={price} number={order.number} />;

        case EOrderStatus.Prolonged:
        case EOrderStatus.Issued:
            return (
                <WrapperDetailInfo
                    config={config}
                    onModalDetailsPayment={onModalDetailsPayment}
                    onModalCalendarPayment={onModalCalendarPayment}
                    isLoading={isLoading}
                    onExtendRent={onOrderValidatePay}
                    dateRent={dateRent}
                >
                    {termUnit === 2 && (
                        <>
                            <Styled.Line color="dark">
                                <span>{t('К оплате по графику')}</span>
                                <span>
                                    {numbers.formatNumberWithSpaces(
                                        numbers.customMathCeil(order.amounts.installment_amount),
                                    )}{' '}
                                    ₸
                                </span>
                            </Styled.Line>
                            <Styled.Line color="dark">
                                <span>{t('Дата следующего платежа')}</span>
                                <span>{order.terms.nearest_pay_date}</span>
                            </Styled.Line>
                        </>
                    )}
                </WrapperDetailInfo>
            );

        case EOrderStatus.Delayed:
        case EOrderStatus.Sent_to_court:
            return (
                <WrapperDetailInfo
                    onModalDetailsPayment={onModalDetailsPayment}
                    onModalCalendarPayment={onModalCalendarPayment}
                    config={config}
                    dateRent={dateRent}
                    isLoading={isLoading}
                    onExtendRent={onOrderValidatePay}
                >
                    {termUnit === 1 || config.status === EOrderStatus.Sent_to_court ? (
                        <Styled.Line big color="error">
                            {config.status !== EOrderStatus.Sent_to_court ? (
                                <span>{t('Текущая задолженность')}</span>
                            ) : (
                                <div>
                                    <span>{t('Задолженность')}</span>
                                    <Tooltip
                                        placement="bottom"
                                        title={<p style={{ color: '#1F2547' }}>{tooltipText}</p>}
                                        color="white"
                                    >
                                        <div>
                                            <ExclamationIcon />
                                        </div>
                                    </Tooltip>
                                </div>
                            )}

                            <span>
                                {numbers.formatNumberWithSpaces(numbers.customMathCeil(order.amounts.return.current))} ₸
                            </span>
                        </Styled.Line>
                    ) : (
                        <>
                            <Styled.Line color="dark">
                                <span>{t('Сумма взноса по графику')}</span>
                                <span>
                                    {numbers.formatNumberWithSpaces(
                                        numbers.customMathCeil(order.amounts.installment_amount),
                                    )}{' '}
                                    ₸
                                </span>
                            </Styled.Line>
                            <Styled.Line color="error">
                                <span>{t('Текущая задолженность')}</span>
                                <span>
                                    {numbers.formatNumberWithSpaces(
                                        numbers.customMathCeil(
                                            order.amounts.return.current - order.amounts.installment_amount,
                                        ),
                                    )}{' '}
                                    ₸
                                </span>
                            </Styled.Line>
                            <Styled.Line big color="dark">
                                <span>{t('Итого к оплате')}</span>
                                <span>
                                    {numbers.formatNumberWithSpaces(
                                        numbers.customMathCeil(order.amounts.return.current),
                                    )}{' '}
                                    ₸
                                </span>
                            </Styled.Line>
                        </>
                    )}
                </WrapperDetailInfo>
            );

        case EOrderStatus.Closed:
            return (
                <WrapperDetailInfo
                    onModalDetailsPayment={onModalDetailsPayment}
                    onModalCalendarPayment={onModalCalendarPayment}
                    config={config}
                    dateRent={dateRent}
                />
            );

        default:
            return <></>;
    }
};
